import React, { memo, useEffect, useMemo } from 'react';

import constants from 'constants/constants';
import { orderBy } from 'lodash';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import useHashmapConverter from 'hooks/useHashmapConverter';

import { useQueryActivityCodesByVersion } from 'api/queries/activityCodesByVersion.query';

import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import classes from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage.module.scss';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { activityCodeMapperUID } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { Select } from 'components/DesignSystem/Select';
import { MenuList } from 'components/DesignSystem/Select/components/MenuList/MenuList';
import { OptionsWithExclude } from 'components/DesignSystem/Select/components/OptionsWithExclude/OptionsWithExclude';

type FormValue = { name: number; isExclude: boolean; alias: string | null };
type Option = {
    label: string;
    value: FormValue;
};

interface IActivityCodesFilterProps {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    versionId?: number | undefined;
    value?: FormValue[];
    isSelected?: boolean;
}

export const ActivityCodesFilter = memo(
    ({
        methods,
        name,
        filtersStatusRef,
        handleFilters,
        versionId,
        value,
        isSelected = true,
    }: IActivityCodesFilterProps) => {
        const { data: activityCodes, isSuccess } = useQueryActivityCodesByVersion({
            latestVersion: versionId,
        });

        const options = useMemo<Option[]>(
            () =>
                activityCodes
                    ? orderBy(
                          activityCodes.map(activityCodeMapperUID),
                          [(item: any) => item['label'].toLowerCase()],
                          ['asc'],
                      )
                    : [],
            [activityCodes],
        );

        useEffect(() => {
            if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
                return;
            }

            signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
        }, [value, name, filtersStatusRef, isSuccess]);

        const handleChange = (selected: readonly ISelectOption<FormValue>[]) => {
            methods?.setValue(
                name,
                selected.map((item) => item.value),
            );
            handleFilters && handleFilters();
        };

        /**
         * 1) VALIDATING the selection against the current options list.
         *    Options depend on the current project version. If a version changed/deleted,
         *    old selections coming from URL params / stored filters might no longer exist
         *    in `options`. We must drop those stale selections to avoid UI bugs.
         *
         * 2) PRESERVING the option object (label, etc.) from `options`,
         *    but OVERRIDING `option.value` with the form value.
         *    Reason: `isExclude` is dynamic (user-controlled) and lives in the form state,
         *    while the fetched `options` always have `isExclude: false`.
         */
        const handleValue = (formValue?: FormValue[]): Option[] => {
            const result: ISelectOption<FormValue>[] = [];
            if (!formValue?.length) return result;

            options.forEach((listOptionElement: ISelectOption<FormValue>) => {
                const currentFormValueElement = formValue.find(
                    (formValueElement) => formValueElement?.name === listOptionElement.value.name,
                );

                if (currentFormValueElement) {
                    result.push({
                        ...listOptionElement,
                        value: {
                            ...currentFormValueElement,
                        },
                    });
                }
            });

            return result;
        };

        return isSelected ? (
            <div className={classes.selectWrapper}>
                <div data-testid={'activity-codes-filter'} className={classes.activityCodesSelectWrapper}>
                    <Controller
                        render={({ field }) => {
                            return (
                                <Select<ISelectOption<FormValue>, true>
                                    {...field}
                                    options={options}
                                    isMulti
                                    components={{
                                        MenuList: MenuList,
                                        Option: OptionsWithExclude,
                                    }}
                                    isOptionDisabled={() => field.value?.length >= constants.MULTI_SELECT_MAX_ITEMS}
                                    value={handleValue(field.value)}
                                    onChange={handleChange}
                                    placeholder={'Select Activity Codes...'}
                                    label={FilterLabels.ACTIVITY_CODES}
                                />
                            );
                        }}
                        name={name}
                        control={methods?.control}
                    />
                </div>
            </div>
        ) : null;
    },
);
